import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { FileImage, Loader2, Upload, RefreshCw, ShieldCheck, AlertTriangle, CheckCircle2, X, ImageOff, Camera, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';

/**
 * Self-service "Re-upload ID Documents" widget.
 *
 * Used in the Member / Merchant profile page so users can replace their
 * government ID + selfie at any time. After upload the backend automatically
 * re-runs OCR via `/api/auth/profile/reupload-id-docs`.
 *
 * Props:
 *   api            - axios instance from useAuth()
 *   currentIdUrl   - existing id_photo_url (for preview only)
 *   currentSelfie  - existing selfie_url (for preview only)
 *   onUpdated      - callback(ocrResult) after a successful reupload
 */
export function ReuploadIdDocsCard({ api, currentIdUrl, currentSelfie, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [idFile, setIdFile] = useState(null);
  const [idPreview, setIdPreview] = useState(currentIdUrl || null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState(currentSelfie || null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const idRef = useRef(null);
  const idCamRef = useRef(null);
  const selfieRef = useRef(null);
  const selfieCamRef = useRef(null);

  const reset = () => {
    setIdFile(null);
    setSelfieFile(null);
    setIdPreview(currentIdUrl || null);
    setSelfiePreview(currentSelfie || null);
    setResult(null);
    [idRef, selfieRef, idCamRef, selfieCamRef].forEach((r) => {
      if (r.current) r.current.value = '';
    });
  };

  const handleClose = () => {
    setOpen(false);
    setTimeout(reset, 250);
  };

  const handlePick = (which) => (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error('File too large. Max 10MB.');
      return;
    }
    if (which === 'id') {
      setIdFile(f);
      setIdPreview(URL.createObjectURL(f));
    } else {
      setSelfieFile(f);
      setSelfiePreview(URL.createObjectURL(f));
    }
  };

  const uploadOne = async (file, purpose) => {
    const fd = new FormData();
    fd.append('file', file);
    if (purpose) fd.append('purpose', purpose);
    const res = await api.post('/api/upload/member-file', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data.url || res.data.file_url;
  };

  const handleSubmit = async () => {
    if (!idFile && !selfieFile) {
      toast.error('Please select at least one new file to upload.');
      return;
    }
    if (!idFile && !currentIdUrl) {
      toast.error('Government ID is required.');
      return;
    }
    if (!selfieFile && !currentSelfie) {
      toast.error('Selfie is required.');
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      // Upload changed files in parallel, reuse existing URLs for unchanged ones.
      const [idUrl, selfieUrl] = await Promise.all([
        idFile ? uploadOne(idFile, 'id-photo') : Promise.resolve(currentIdUrl),
        selfieFile ? uploadOne(selfieFile, 'selfie') : Promise.resolve(currentSelfie),
      ]);
      // Kicks off async OCR — server responds within ~1s.
      await api.post('/api/auth/profile/reupload-id-docs', {
        id_photo_url: idUrl, selfie_url: selfieUrl,
      });
      toast.info('OCR re-verification started. This usually takes 20-60 seconds…');

      // Poll /me every 3s for up to 3 minutes, until verification_status flips
      // out of "processing".
      const MAX_POLLS = 60;
      let finalUser = null;
      for (let i = 0; i < MAX_POLLS; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data: me } = await api.get('/api/auth/me');
        if (me?.verification_status && me.verification_status !== 'processing') {
          finalUser = me;
          break;
        }
      }
      if (!finalUser) {
        toast.warning('Verification is still processing — please refresh in a minute.');
        onUpdated?.();
        return;
      }
      const verif = finalUser.id_verification || {};
      const verified = finalUser.verification_status === 'verified';
      setResult({
        verified,
        id_name: verif.id_name,
        confidence: verif.confidence,
        reason: verif.reason,
      });
      if (verified) {
        toast.success('Identity verified!');
      } else {
        toast.warning('Documents uploaded — pending admin review.');
      }
      onUpdated?.(finalUser);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reupload failed');
    }
    setSubmitting(false);
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="border-[#00E5FF]/30 text-[#00E5FF] hover:bg-[#00E5FF]/10 hover:text-[#00E5FF]"
        data-testid="reupload-id-docs-btn"
      >
        <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
        Re-upload ID & Selfie
      </Button>

      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : handleClose())}>
        <DialogContent
          className="bg-[#041A3B] border-white/10 max-w-lg"
          data-testid="reupload-id-docs-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileImage className="w-5 h-5 text-[#00E5FF]" />
              Re-upload Identity Documents
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-[#A0B4CC] text-xs leading-relaxed">
              Your name on the ID must match your registered name. After upload we&apos;ll
              automatically re-run our OCR check.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <SlotPicker
                label="Government ID"
                preview={idPreview}
                fileRef={idRef}
                cameraRef={idCamRef}
                onChange={handlePick('id')}
                captureMode="environment"
                hint="Front of any government-issued ID"
                testId="reupload-id"
              />
              <SlotPicker
                label="Selfie"
                preview={selfiePreview}
                fileRef={selfieRef}
                cameraRef={selfieCamRef}
                onChange={handlePick('selfie')}
                captureMode="user"
                hint="Clear photo of your face"
                testId="reupload-selfie"
              />
            </div>

            {result && (
              <ResultPanel result={result} />
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={handleClose} className="text-[#A0B4CC]" data-testid="reupload-cancel-btn">
              {result ? 'Close' : 'Cancel'}
            </Button>
            {!result && (
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-[#00E5FF] text-[#000B18] hover:bg-[#00E5FF]/90"
                data-testid="reupload-submit-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Re-running OCR…</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" /> Submit & Re-verify</>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function SlotPicker({ label, preview, fileRef, cameraRef, onChange, captureMode, hint, testId }) {
  const [broken, setBroken] = useState(false);
  const [lastPreview, setLastPreview] = useState(preview);
  // Reset broken-image flag whenever the preview URL changes.
  if (preview !== lastPreview) {
    setLastPreview(preview);
    setBroken(false);
  }

  const hasValidPreview = !!preview && !broken;
  const hasBrokenPreview = !!preview && broken;

  const openFile = () => fileRef.current?.click();
  const openCamera = () => cameraRef.current?.click();

  return (
    <div data-testid={`${testId}-slot`}>
      <Label className="text-[#A0B4CC] text-xs mb-1.5 block">{label}</Label>

      {/* Preview area — clicking the empty/broken state defaults to file picker
          so users still have a single-tap fallback. */}
      <div
        className="rounded-lg border border-dashed border-white/15 bg-[#000B18] overflow-hidden"
      >
        <button
          type="button"
          onClick={hasValidPreview ? undefined : openFile}
          className={`block w-full h-32 flex items-center justify-center overflow-hidden ${
            hasValidPreview ? 'cursor-default' : 'cursor-pointer hover:border-[#00E5FF]/40'
          }`}
          aria-label={hasValidPreview ? `${label} preview` : `Upload ${label}`}
          data-testid={`${testId}-preview-area`}
        >
          {hasValidPreview ? (
            <img src={preview} alt={label} onError={() => setBroken(true)}
              className="w-full h-full object-cover" />
          ) : (
            <div className="text-center px-2" data-testid={`${testId}-placeholder`}>
              {hasBrokenPreview ? (
                <>
                  <ImageOff className="w-7 h-7 text-[#FFA726] mx-auto mb-1" />
                  <p className="text-[#FFA726] text-[11px] font-medium leading-tight">Previous image unavailable</p>
                  <p className="text-[#5C7599] text-[10px] mt-0.5">Choose a file or take a photo below</p>
                </>
              ) : (
                <>
                  <FileImage className="w-7 h-7 text-[#5C7599] mx-auto mb-1" />
                  <p className="text-[#A0B4CC] text-[11px]">{hint || 'No file selected'}</p>
                </>
              )}
            </div>
          )}
        </button>

        {/* Explicit Camera / File buttons — visible at all times so users
            always know how to (re)attach a file. */}
        <div className="grid grid-cols-2 border-t border-white/10 text-[11px] font-medium">
          <button
            type="button"
            onClick={openFile}
            className="flex items-center justify-center gap-1.5 py-2.5 text-[#A0B4CC] hover:bg-[#00E5FF]/10 hover:text-[#00E5FF] transition-colors border-r border-white/10"
            data-testid={`${testId}-pick-file-btn`}
          >
            <FolderOpen className="w-3.5 h-3.5" />
            <span>{hasValidPreview ? 'Replace from files' : 'Choose file'}</span>
          </button>
          <button
            type="button"
            onClick={openCamera}
            className="flex items-center justify-center gap-1.5 py-2.5 text-[#A0B4CC] hover:bg-[#00E5FF]/10 hover:text-[#00E5FF] transition-colors"
            data-testid={`${testId}-pick-camera-btn`}
          >
            <Camera className="w-3.5 h-3.5" />
            <span>{hasValidPreview ? 'Retake photo' : 'Use camera'}</span>
          </button>
        </div>
      </div>

      {/* Two hidden inputs — one defaults to file chooser (no capture), the
          other forces the device camera with the appropriate facing mode. */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onChange}
        data-testid={`${testId}-file-input`}
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture={captureMode}
        className="hidden"
        onChange={onChange}
        data-testid={`${testId}-camera-input`}
      />
    </div>
  );
}

function ResultPanel({ result }) {
  const verified = result.verified;
  return (
    <div
      className={`rounded-lg p-3 border ${
        verified
          ? 'bg-[#00E676]/10 border-[#00E676]/30'
          : 'bg-[#FFEA00]/10 border-[#FFEA00]/30'
      }`}
      data-testid="reupload-result"
    >
      <div className="flex items-start gap-2">
        {verified ? (
          <CheckCircle2 className="w-4 h-4 text-[#00E676] flex-shrink-0 mt-0.5" />
        ) : (
          <AlertTriangle className="w-4 h-4 text-[#FFEA00] flex-shrink-0 mt-0.5" />
        )}
        <div className="text-xs">
          <p className={`font-semibold ${verified ? 'text-[#00E676]' : 'text-[#FFEA00]'}`}>
            {verified ? 'Identity Verified' : 'Pending Admin Review'}
          </p>
          {result.id_name && (
            <p className="text-[#A0B4CC] mt-0.5">
              Name on ID: <span className="text-white">{result.id_name}</span>
            </p>
          )}
          {typeof result.confidence === 'number' && (
            <p className="text-[#A0B4CC]">
              OCR confidence: <span className="text-white">{Math.round((result.confidence || 0) * (result.confidence > 1 ? 1 : 100))}%</span>
            </p>
          )}
          {!verified && result.reason && (
            <p className="text-[#A0B4CC] mt-1 italic">{result.reason}</p>
          )}
        </div>
      </div>
    </div>
  );
}
